/**
 * basesApi — chamadas à API de Bases de Referência (M3) sobre o ApiClient base.
 * Contrato: docs/api-contracts.md → M3; todas as respostas no envelope `{ data }`.
 *
 *  - GET  /bases                   → versões de base (INEP/IBGE/DDD)
 *  - POST /bases/{tipo}/upload-url → URL PUT pré-assinada para o arquivo
 *  - POST /bases/{tipo}            → 202 { cargaId, tipo, versao, status }
 *  - GET  /bases/cargas/{id}       → estado da carga (polling)
 *  - GET/PATCH /parametros         → limiares de validação
 *
 * Auth: grupo `admin` (a API valida).
 */

import { ApiClient } from './apiClient';
import {
  BaseVersaoItem,
  CargaBaseResponse,
  EstadoCargaBase,
  Parametros,
  PatchParametros,
  TipoBaseUpload,
  UploadUrlBase,
} from './bases-types';

interface Envelope<T> {
  readonly data: T;
}

export class BasesApi {
  constructor(private readonly client: ApiClient) {}

  /** GET /bases — lista versões de base (INEP/IBGE/DDD). */
  async listar(): Promise<readonly BaseVersaoItem[]> {
    const resp = await this.client.request<Envelope<readonly BaseVersaoItem[]>>('/bases');
    return resp.data;
  }

  /** POST /bases/{tipo}/upload-url — URL PUT pré-assinada para o arquivo da base. */
  async pedirUploadUrl(tipo: TipoBaseUpload, filename: string, contentType: string): Promise<UploadUrlBase> {
    const resp = await this.client.request<Envelope<UploadUrlBase>>(
      `/bases/${encodeURIComponent(tipo)}/upload-url`,
      { method: 'POST', body: JSON.stringify({ filename, contentType }) },
    );
    return resp.data;
  }

  /** POST /bases/{tipo} — inicia a carga da nova versão a partir da chave devolvida pela upload-url. */
  async criarCarga(tipo: TipoBaseUpload, arquivoS3Key: string, notas?: string): Promise<CargaBaseResponse> {
    const resp = await this.client.request<Envelope<CargaBaseResponse>>(`/bases/${encodeURIComponent(tipo)}`, {
      method: 'POST',
      body: JSON.stringify(notas ? { arquivoS3Key, notas } : { arquivoS3Key }),
    });
    return resp.data;
  }

  /** GET /bases/cargas/{id} — estado da carga (polling). */
  async estadoCarga(cargaId: string): Promise<EstadoCargaBase> {
    const resp = await this.client.request<Envelope<EstadoCargaBase>>(
      `/bases/cargas/${encodeURIComponent(cargaId)}`,
    );
    return resp.data;
  }

  /** GET /parametros — limiares de validação. */
  async lerParametros(): Promise<Parametros> {
    const resp = await this.client.request<Envelope<Parametros>>('/parametros');
    return resp.data;
  }

  /** PATCH /parametros — atualização parcial dos limiares. */
  async atualizarParametros(patch: PatchParametros): Promise<Parametros> {
    const resp = await this.client.request<Envelope<Parametros>>('/parametros', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return resp.data;
  }
}
